import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { DimensionInput } from 'dashboards/types/dimension';
import { FilterInput } from 'dashboards/types/filter';
import {
  TimeRangeInput,
  TimeSeriesGranularity,
  TimeSeriesResponse,
} from 'dashboards/types/time';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';

type UseTimeSeriesParams = {
  metricName: string;
  granularity: TimeSeriesGranularity;
  timeRange: TimeRangeInput;
  dimensions?: Array<DimensionInput>;
  filters: Array<FilterInput>;
  timeZone: string;
};

type UseTimeSeriesOptions = UseQueryOptions<TimeSeriesResponse, APIError> & {
  params: UseTimeSeriesParams;
};

export function useTimeSeries({
  queryKey = ['timeseries'],
  params: { dimensions, ...params },
  ...options
}: UseTimeSeriesOptions) {
  const { applicationID, client } = useAPI();

  const groupBy = Array.isArray(dimensions)
    ? dimensions.map(({ columnName }) => columnName)
    : undefined;

  const queryParams = {
    ...params,
    timeGranularity: params.granularity,
    groupBy,
  };

  // FIXME api-server should validate this
  const invalidTimeRange =
    new Date(queryParams.timeRange.start) >
    new Date(queryParams.timeRange.stop);

  return useAuthenticatedQuery(
    [...queryKey, queryParams],
    async () => {
      if (invalidTimeRange) {
        throw new Error(
          'Invalid time range: start date must be before end date'
        );
      }

      const { data } = await client.post<TimeSeriesResponse>(
        `/apps/${applicationID}/user_metrics/timeseries`,
        queryParams
      );

      return data;
    },
    { ...options }
  );
}
